import { css } from "@emotion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { Alert, Button, Heading, Icon, Icons, Link, LinkButton, Loading, Text, Label, Popover, Checkbox, DialogTrigger } from "@phoenix/components";
import { SelectChevronUpDownIcon } from "@phoenix/components/core/icon";
import { useNotifyError, useNotifySuccess } from "@phoenix/contexts";

import { authFetch } from "../../authFetch";
import { prependBasename } from "../../utils/routingUtils";
import {
  describeTargetScope,
  GLOBAL_SCOPE_STORAGE_KEY,
  isEvaluationScope,
  readStoredScope,
  scopeLabel,
  type EvaluationScope,
} from "./evaluationScope";
import { extractApiErrorMessage, fetchApi } from "./templateApi";

interface EvaluatorTemplate {
  id: string;
  name: string;
  category: string;
  description: string;
  metric_name?: string;
  inputs?: string[];
  tier?: string;
}

interface TemplateProjectMetric {
  template_id: string;
  project_name: string;
  judge_calls: number;
  judge_errors: number;
  error_rate_pct: number;
  avg_latency_ms: number;
  annotations_written: number;
  budget_skips: number;
}

// Live (online) evaluation is opt-in per (template, project): every template
// is user-actionable from this page, and nothing runs until Start is pressed.
// This replaces the old "Always-on (centrally managed)" T0/T1 behavior that
// ran every template against every project and burned judge/LLM budget.

interface MonitorTraceResult {
  trace_id: string;
  perplexity?: number | null;
  result?: string;
}

interface MonitorLastResult {
  last_evaluation?: {
    result?: string;
    status?: string;
    score?: number;
    reason?: string;
  };
  summary?: {
    total?: number;
    passed?: number;
    failed?: number;
    skipped?: number;
  };
  traces?: MonitorTraceResult[];
  threshold?: number;
}

interface BulkStartResponseBody {
  detail?: string;
  enabled?: string[];
  already_enabled?: string[];
  not_found?: string[];
}

interface MonitorStatus {
  template_id: string;
  status: "running" | "stopped";
  project_name: string | null;
  started_at?: string | null;
  last_check_at?: string | null;
  processed_traces?: number;
  annotations_logged?: number;
  new_traces?: number;
  poll_interval?: number;
  stopped_at?: string | null;
  last_result?: MonitorLastResult | null;
  level?: string | null;
  span_kind?: string | null;
}

// UI-level evaluation scope is defined in ./evaluationScope and owned by the
// Templates landing page, so a single choice applies to every evaluation.

interface LiveActivityEntry {
  id: string;
  timestamp: string;
  title: string;
  description: string;
  status: "info" | "success";
}

const normalizeMonitorStatus = (status: string | undefined | null): "running" | "stopped" => {
  const normalized = status?.toString().trim().toLowerCase() ?? "";
  return ["running", "run", "started", "active", "enabled", "true", "1"].includes(normalized)
    ? "running"
    : "stopped";
};

const parseMonitorStatus = (rawStatus: unknown): "running" | "stopped" => {
  if (rawStatus === undefined || rawStatus === null) {
    return "stopped";
  }
  if (typeof rawStatus === "boolean") {
    return rawStatus ? "running" : "stopped";
  }
  if (typeof rawStatus === "number") {
    return rawStatus !== 0 ? "running" : "stopped";
  }
  if (typeof rawStatus === "string") {
    return normalizeMonitorStatus(rawStatus);
  }
  if (typeof rawStatus === "object" && rawStatus !== null) {
    const record = rawStatus as Record<string, unknown>;
    if ("status" in record) {
      return parseMonitorStatus(record.status);
    }
    if ("state" in record) {
      return parseMonitorStatus(record.state);
    }
    if ("running" in record) {
      return parseMonitorStatus(record.running);
    }
  }
  return "stopped";
};

const buildMonitorStatus = (raw: unknown): MonitorStatus | null => {
  if (typeof raw !== "object" || raw === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const templateId =
    typeof record.template_id === "string"
      ? record.template_id
      : typeof record.templateId === "string"
      ? record.templateId
      : typeof record.id === "string"
      ? record.id
      : undefined;
  if (!templateId) {
    return null;
  }

  return {
    template_id: templateId,
    status: parseMonitorStatus(record.status ?? record.state ?? record.running),
    project_name: typeof record.project_name === "string" ? record.project_name : null,
    started_at: typeof record.started_at === "string" ? record.started_at : null,
    last_check_at: typeof record.last_check_at === "string" ? record.last_check_at : null,
    processed_traces: typeof record.processed_traces === "number" ? record.processed_traces : 0,
    annotations_logged: typeof record.annotations_logged === "number" ? record.annotations_logged : 0,
    new_traces: typeof record.new_traces === "number" ? record.new_traces : 0,
    poll_interval: typeof record.poll_interval === "number" ? record.poll_interval : 0,
    stopped_at: typeof record.stopped_at === "string" ? record.stopped_at : null,
    last_result: (record.last_result as MonitorLastResult | null) ?? null,
    level: typeof record.level === "string" ? record.level : null,
    span_kind: typeof record.span_kind === "string" ? record.span_kind : null,
  };
};

// localStorage can throw when storage is blocked (private mode, third-party
// cookie settings). These wrappers keep a blocked store from crashing render
// or an event handler; the in-memory state remains authoritative.
const readStoredValue = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const storeValue = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage unavailable; the in-memory selection still applies this session.
  }
};

const pageContainer = css`
  display: grid;
  gap: var(--global-dimension-size-75);
  padding: var(--global-dimension-size-100);
  padding-bottom: 140px;
  width: 100%;
  min-height: 100vh;
  height: auto;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
`;

const topActions = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--global-dimension-size-75);
  flex-wrap: wrap;
`;

const breadcrumbRow = css`
  display: flex;
  gap: var(--global-dimension-size-75);
  align-items: center;
  flex-wrap: wrap;
`;

const breadcrumbLink = css`
  font-size: 0.875rem;
  color: var(--color-text-500);
  text-decoration: none;
  &:hover {
    color: var(--color-text-900);
    text-decoration: underline;
  }
`;

const headerRow = css`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: var(--global-dimension-size-100);
  align-items: flex-start;
`;

const categoryIntro = css`
  display: grid;
  gap: var(--global-dimension-size-50);
`;

const sectionCard = css`
  display: grid;
  background: var(--ac-global-color-grey-50);
  border: 1px solid var(--ac-global-color-grey-200);
  border-radius: var(--ac-global-rounding-medium);
  padding: var(--global-dimension-size-100);
  overflow: visible;
  height: auto;
`;

const evaluatorCard = css`
  display: flex;
  flex-direction: column;
  gap: var(--global-dimension-size-75);
  background: var(--global-color-gray-75);
  border: 1px solid var(--ac-global-color-grey-200);
  border-radius: var(--ac-global-rounding-medium);
  padding: var(--global-dimension-size-100);
  box-sizing: border-box;
`;

const activeCardBorder = css`
  border: 1px solid rgba(34, 197, 94, 0.4) !important;
  background: rgba(34, 197, 94, 0.02) !important;
`;

const cardHeader = css`
  display: flex;
  gap: var(--global-dimension-size-50);
  align-items: flex-start;
`;

const cardIcon = css`
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  border-radius: 14px;
  background: rgba(59, 130, 246, 0.12);
`;

const badgeRow = css`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
`;

const chip = css`
  display: inline-flex;
  border-radius: 999px;
  padding: 0.35rem 0.7rem;
  font-size: 0.75rem;
  font-weight: 600;
  background: rgba(15, 23, 42, 0.06);
  color: var(--color-text-700);
`;

const sectionLabel = css`
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.75rem;
  color: var(--color-text-400);
`;

const pageGrid = css`
  display: grid;
  grid-template-columns: minmax(0, 2fr) 360px;
  gap: var(--global-dimension-size-100);
  align-items: start;
  width: 100%;
  height: auto;
`;

const mainContent = css`
  display: flex;
  flex-direction: column;
  gap: var(--global-dimension-size-100);
  min-width: 0;
  width: 100%;
`;

const cardGrid = css`
  display: grid;
  gap: var(--global-dimension-size-75);
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
`;

const statusBadge = css`
  display: inline-flex;
  padding: 0.5rem 0.85rem;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 700;
`;

const statusSuccess = css`
  ${statusBadge};
  background: rgba(34, 197, 94, 0.14);
  color: var(--color-success-900);
`;

const statusStopped = css`
  ${statusBadge};
  background: rgba(100, 116, 139, 0.12);
  color: var(--color-text-600);
`;

const metricRow = css`
  display: grid;
  gap: var(--global-dimension-size-50);
  grid-template-columns: repeat(2, minmax(0, 1fr));
`;

const metricCard = css`
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.04);
  padding: var(--global-dimension-size-50);
`;

const summaryRow = css`
  display: grid;
  gap: var(--global-dimension-size-100);
`;

const summaryPanel = css`
  display: grid;
  gap: var(--global-dimension-size-100);
  align-self: start;
  height: fit-content;
  overflow: visible;
`;

const actionRow = css`
  display: flex;
  gap: var(--global-dimension-size-50);
  flex-wrap: wrap;
`;

const summaryCard = css`
  border-radius: 28px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: var(--global-color-gray-75);
  padding: var(--global-dimension-size-75);
  display: grid;
  gap: var(--global-dimension-size-50);
`;

const liveActivityList = css`
  display: grid;
  gap: var(--global-dimension-size-50);
`;

const liveActivityItem = css`
  display: grid;
  gap: var(--global-dimension-size-25);
  padding: var(--global-dimension-size-50);
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(15, 23, 42, 0.02);
`;

const liveActivityHeader = css`
  display: flex;
  justify-content: space-between;
  gap: var(--global-dimension-size-50);
  align-items: center;
`;

const timestampLabel = css`
  font-size: 0.75rem;
  color: var(--color-text-400);
`;

const emptyState = css`
  display: grid;
  align-items: center;
  justify-content: center;
  gap: var(--global-dimension-size-75);
  padding: var(--global-dimension-size-100);
  min-height: 200px;
  color: var(--color-text-500);
`;

const fieldWrapper = css`
  display: flex;
  flex-direction: column;
  gap: var(--global-dimension-size-50);
  width: 100%;

  .react-aria-Select, .react-aria-TextField {
    display: flex;
    flex-direction: column;
    gap: var(--global-dimension-size-50);
    width: 100%;
  }

  button {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-align: left;
    box-sizing: border-box;
  }

  input {
    width: 100%;
    box-sizing: border-box;
  }
`;

const labelStyle = css`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text-800);
`;

const selectedProjectsContainer = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: var(--global-color-gray-75, #ffffff);
  border: 1px solid var(--ac-global-color-grey-200, rgba(15, 23, 42, 0.08));
  border-radius: var(--ac-global-rounding-medium, 12px);
  padding: var(--global-dimension-size-100, 16px);
  margin-top: var(--global-dimension-size-75, 12px);
  width: 100%;
  box-sizing: border-box;
`;

const selectedProjectsHeader = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const selectedProjectsTitle = css`
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--color-text-800, #1e293b);
`;

const trashIconStyle = css`
  width: 14px;
  height: 14px;
  color: currentColor;
`;

const folderIconStyle = css`
  width: 14px;
  height: 14px;
  color: currentColor;
  opacity: 0.8;
`;

const badgesWrapper = css`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const badgeItemStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 0.875rem;
  font-weight: 500;
  background: rgba(13, 148, 136, 0.05); /* light teal background */
  color: #0d9488; /* teal text */
  border: 1px solid rgba(13, 148, 136, 0.18);
  transition: all 0.2s ease;

  &:hover {
    background: rgba(13, 148, 136, 0.1);
    border-color: rgba(13, 148, 136, 0.25);
  }
`;

const badgeRemoveButtonStyle = css`
  background: transparent;
  border: none;
  color: rgba(13, 148, 136, 0.6);
  cursor: pointer;
  padding: 0;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  transition: all 0.2s;

  &:hover {
    background-color: rgba(13, 148, 136, 0.15);
    color: #0d9488;
  }
`;

const clearAllButtonStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: var(--ac-global-color-blue-600, #2563eb);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
  width: auto;
  justify-content: center;

  &:hover {
    background: rgba(37, 99, 235, 0.06);
    text-decoration: underline;
  }
`;

const categoryTitle = (category: string) => {
  if (category === "sql_evaluation") {
    return "SQL Evaluation";
  }
  if (category === "rag" || category === "rag_evaluation") {
    return "RAG Evaluation";
  }
  if (category === "behavior_evaluation") {
    return "Behavior Evaluations";
  }
  if (category === "output_format") {
    return "Output Format Evaluations";
  }
  if (category === "model_graded_metrics") {
    return "Model-graded metrics";
  }
  return category
    .replace(/_/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
};

const categoryDescription = (category: string) =>
  `Explore the ${categoryTitle(category)} template set and run evaluations against your project traces.`;

const iconForCategory = (category: string) => {
  switch (category) {
    case "sql_evaluation":
      return <Icons.DatabaseOutline />;
    case "deterministic_metrics":
      return <Icons.CheckmarkCircleOutline />;
    case "tool_call":
      return <Icons.WrenchOutline />;
    case "rag":
    case "rag_evaluation":
      return <Icons.SearchOutline />;
    case "reasoning":
      return <Icons.Info />;
    case "agent":
      return <Icons.WrenchOutline />;
    case "safety":
      return <Icons.SearchOutline />;
    case "test":
      return <Icons.SearchOutline />;
    case "behavior_evaluation":
      return <Icons.Brain />;
    case "output_format":
      return <Icons.TemplateOutline />;
    case "Knowledge_accuracy":
      return <Icons.BlockJSON />;
    case "Observability":
      return <Icons.EyeOutline />;
    case "Consistency_evaluation":
      return <Icons.RepeatOutline />;
    case "model_graded_metrics":
      return <Icons.ShieldOutline />;
    default: 
      return <Icons.TemplateOutline />;
  }
};


interface EvalModelConfig {
  provider: string;
  model: string;
  apiKey: string;
  baseUrl: string;
  temperature: number;
  maxCompletionTokens: number;
  topP: number;
  seed: string;
}

const PROVIDER_METADATA: Record<string, { name: string; baseUrl: string; defaultModel: string; models: string[] }> = {
  google: {
    name: "Google / Gemini",
    baseUrl: "https://generativelanguage.googleapis.com/v1beta/openai/",
    defaultModel: "gemini-2.5-flash",
    models: ["gemini-2.5-flash", "gemini-1.5-pro", "gemini-1.5-flash", "gemini-2.0-flash-exp"],
  },
  openai: {
    name: "OpenAI",
    baseUrl: "https://api.openai.com/v1",
    defaultModel: "gpt-4o",
    models: ["gpt-4o", "gpt-4o-mini", "gpt-4-turbo", "gpt-3.5-turbo", "o1", "o1-mini", "o3-mini"],
  },
  anthropic: {
    name: "Anthropic",
    baseUrl: "https://api.anthropic.com/v1",
    defaultModel: "claude-3-5-sonnet-20241022",
    models: ["claude-3-5-sonnet-20241022", "claude-3-5-haiku-20241022", "claude-3-haiku-20240307", "claude-3-opus-20240229"],
  },
  groq: {
    name: "Groq",
    baseUrl: "https://api.groq.com/openai/v1",
    defaultModel: "llama-3.3-70b-versatile",
    models: ["llama-3.3-70b-versatile", "llama-3.1-8b-instant", "mixtral-8x7b-32768", "gemma2-9b-it"],
  },
  ollama: {
    name: "Ollama",
    baseUrl: "http://localhost:11434/v1",
    defaultModel: "llama3",
    models: ["llama3", "llama3.2", "mistral", "qwen2.5", "phi3"],
  },
  deepseek: {
    name: "DeepSeek",
    baseUrl: "https://api.deepseek.com/v1",
    defaultModel: "deepseek-chat",
    models: ["deepseek-chat", "deepseek-coder", "deepseek-reasoner"],
  },
  azure_openai: {
    name: "Azure OpenAI",
    baseUrl: "",
    defaultModel: "gpt-4o",
    models: ["gpt-4o", "gpt-4o-mini", "gpt-4"],
  },
  custom: {
    name: "Custom Provider",
    baseUrl: "",
    defaultModel: "custom-model",
    models: ["custom-model"],
  },
};

export function TemplateCategoryPage() {
  const { category } = useParams();
  const notifySuccess = useNotifySuccess();
  const notifyError = useNotifyError();
  const [templates, setTemplates] = useState<EvaluatorTemplate[]>([]);
  const [projects, setProjects] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionPending, setActionPending] = useState<string | null>(null);
  const [projectName, setProjectName] = useState(() => {
    return (category ? readStoredValue(`monitor_project_name_${category}`) : null) || "__all__";
  });
  const [monitorStatuses, setMonitorStatuses] = useState<Record<string, MonitorStatus>>({});
  const [templateMetrics, setTemplateMetrics] = useState<Record<string, TemplateProjectMetric>>({});
  const [expectedSubstrings, setExpectedSubstrings] = useState<Record<string, string>>({});
  const [criterions, setCriterions] = useState<Record<string, string>>({});
  const [perplexityThresholds, setPerplexityThresholds] = useState<Record<string, string>>({});
  const [evaluationScope, setEvaluationScope] = useState<EvaluationScope>(() =>
    readStoredScope()
  );

  // Seed from the server-side global default (defaults.scope) when this
  // browser has no local choice yet. The scope itself is owned by the
  // Templates landing page and applies to every evaluation.
  useEffect(() => {
    let active = true;
    async function fetchScopeDefault() {
      try {
        const response = await fetchApi("/v1/templates/settings");
        if (!response.ok) {
          return;
        }
        const data = await response.json().catch(() => null);
        const scope = data?.scope;
        if (!isEvaluationScope(scope)) {
          return;
        }
        const stored = readStoredValue(GLOBAL_SCOPE_STORAGE_KEY);
        if (active && !isEvaluationScope(stored)) {
          setEvaluationScope(scope);
        }
      } catch {
        // The default is supplementary; the built-in trace default applies.
      }
    }
    fetchScopeDefault();
    return () => {
      active = false;
    };
  }, [category]);

  useEffect(() => {
    let active = true;
    async function fetchProjects() {
      try {
        const response = await authFetch(prependBasename("/v1/projects"));
        if (response.ok) {
          const data = await response.json();
          if (active) {
            setProjects(data.data || []);
          }
        }
      } catch {
        // Projects are required to start evaluation; a failed load leaves the
        // selector empty rather than surfacing a toast on page load.
      }
    }
    fetchProjects();
    return () => {
      active = false;
    };
  }, []);
  const [liveActivity, setLiveActivity] = useState<LiveActivityEntry[]>([]);
  const previousMonitorStatuses = useRef<Record<string, MonitorStatus>>({});

  const statusErrorRef = useRef<string | null>(null);
  // --- Evaluation Model Configuration State ---
  const [evalModelConfig, setEvalModelConfig] = useState<EvalModelConfig>(() => {
    const saved =
      (category ? localStorage.getItem(`eval_model_config_${category}`) : null) ||
      localStorage.getItem("eval_model_config_global");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const rawKey = typeof parsed.apiKey === "string" ? parsed.apiKey : "";
        const cleanKey = (rawKey.trim() === "Hide" || rawKey.trim() === "Show") ? "" : rawKey;
        return {
          provider: parsed.provider || "google",
          model: parsed.model || "gemini-2.5-flash",
          apiKey: cleanKey,
          baseUrl: parsed.baseUrl ?? "https://generativelanguage.googleapis.com/v1beta/openai/",
          temperature: typeof parsed.temperature === "number" ? parsed.temperature : 0,
          maxCompletionTokens: parsed.maxCompletionTokens || 1000,
          topP: typeof parsed.topP === "number" ? parsed.topP : 1.0,
          seed: parsed.seed ?? "",
        };
      } catch {
        // ignore
      }
    }
    return {
      provider: "google",
      model: "gemini-2.5-flash",
      apiKey: "",
      baseUrl: "https://generativelanguage.googleapis.com/v1beta/openai/",
      temperature: 0,
      maxCompletionTokens: 1000,
      topP: 1.0,
      seed: "",
    };
  });

  const [showAdvancedConfig, setShowAdvancedConfig] = useState(true);
  const [showApiKey, setShowApiKey] = useState(false);
  const [isCustomModel, setIsCustomModel] = useState(() => evalModelConfig.provider === "custom");
  const [isValidatingModel, setIsValidatingModel] = useState(false);
  const [validationStatus, setValidationStatus] = useState<{ success: boolean; message: string } | null>(null);

  // Sync to localStorage on every change
  useEffect(() => {
    try {
      const serialized = JSON.stringify(evalModelConfig);
      if (category) {
        localStorage.setItem(`eval_model_config_${category}`, serialized);
      }
      localStorage.setItem("eval_model_config_global", serialized);
    } catch {
      // ignore
    }
  }, [evalModelConfig, category]);

  const handleProviderChange = (newProvider: string) => {
    const pMeta = PROVIDER_METADATA[newProvider] || PROVIDER_METADATA.custom;
    setEvalModelConfig((prev) => ({
      ...prev,
      provider: newProvider,
      model: pMeta.defaultModel,
      baseUrl: pMeta.baseUrl || prev.baseUrl,
    }));
    setIsCustomModel(newProvider === "custom");
    setValidationStatus(null);
  };

  const handleValidateModel = async () => {
    setIsValidatingModel(true);
    setValidationStatus(null);
    try {
      const payload = {
        provider: evalModelConfig.provider,
        model: evalModelConfig.model,
        api_key: evalModelConfig.apiKey || undefined,
        base_url: evalModelConfig.baseUrl || undefined,
        temperature: evalModelConfig.temperature,
        max_tokens: evalModelConfig.maxCompletionTokens,
        top_p: evalModelConfig.topP,
        seed: evalModelConfig.seed ? parseInt(evalModelConfig.seed, 10) : undefined,
      };
      const response = await fetchApi("/v1/templates/validate-model", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => null);
      if (response.ok && data?.success) {
        setValidationStatus({ success: true, message: data.message || "Model validated successfully." });
        notifySuccess({
          title: "Model Validated",
          message: data.message || `Successfully connected to ${evalModelConfig.provider} (${evalModelConfig.model})`,
        });
      } else {
        const errMsg = data?.message || data?.detail || "Validation failed";
        setValidationStatus({ success: false, message: errMsg });
        notifyError({
          title: "Validation Failed",
          message: errMsg,
        });
      }
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : "Network error during validation";
      setValidationStatus({ success: false, message: errMsg });
      notifyError({
        title: "Validation Failed",
        message: errMsg,
      });
    } finally {
      setIsValidatingModel(false);
    }
  };


  const fetchMonitorStatuses = useCallback(async () => {
    try {
      const response = await fetchApi("/v1/templates/status");
      const rawData = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(rawData?.detail || response.statusText);
      }
      const rawArray = Array.isArray(rawData) ? rawData : Object.values(rawData);
      const parsedStatuses = (rawArray as unknown[]).reduce<Record<string, MonitorStatus>>((map, item) => {
        const status = buildMonitorStatus(item);
        if (status) {
          const existing = map[status.template_id];
          // Live evaluation is per (template, project): a template is
          // "running" when any selected project is running, so a running row
          // wins over a stopped history row for the same template.
          if (!existing || status.status === "running") {
            map[status.template_id] = status;
          }
        }
        return map;
      }, {} as Record<string, MonitorStatus>);

      const previousStatuses = previousMonitorStatuses.current;
      const hasPreviousStatuses = Object.keys(previousStatuses).length > 0;
      const events: LiveActivityEntry[] = [];

      Object.entries(parsedStatuses).forEach(([templateId, status]) => {
        const previous = previousStatuses[templateId];
        if (hasPreviousStatuses && (!previous || previous.status !== status.status)) {
          events.push({
            id: `${templateId}-${Date.now()}-${status.status}`,
            timestamp: status.last_check_at ?? new Date().toISOString(),
            title:
              status.status === "running"
                ? `Started monitoring ${templateId}`
                : `Stopped monitoring ${templateId}`,
            description:
              status.status === "running"
                ? `Project: ${status.project_name ?? "unknown"}`
                : `Stopped at ${status.stopped_at ?? status.last_check_at ?? "unknown"}`,
            status: status.status === "running" ? "success" : "info",
          });
        }

        if (status.status === "running" && (status.new_traces ?? 0) > 0) {
          events.push({
            id: `${templateId}-${Date.now()}-new-traces-${status.new_traces}`,
            timestamp: status.last_check_at ?? new Date().toISOString(),
            title: `${status.new_traces} new traces detected for ${templateId}`,
            description: `Processed ${status.processed_traces ?? 0} traces so far.`,
            status: "info",
          });
        }
      });

      previousMonitorStatuses.current = parsedStatuses;
      setMonitorStatuses(parsedStatuses);
      if (events.length > 0) {
        setLiveActivity((existing) => [...events, ...existing].slice(0, 20));
      }

      statusErrorRef.current = null;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      if (statusErrorRef.current !== errorMessage) {
        notifyError({
          title: "Failed to load monitoring status",
          message: errorMessage,
        });
        statusErrorRef.current = errorMessage;
      }
    }
  }, [notifyError]);

  const fetchTemplateMetrics = useCallback(async () => {
    try {
      const response = await fetchApi("/v1/system/templates");
      if (!response.ok) {
        return;
      }
      const data = await response.json().catch(() => null);
      const rows: TemplateProjectMetric[] = Array.isArray(data?.rows) ? data.rows : [];
      const selectedProjects = projectName
        .split(",")
        .map((name) => name.trim())
        .filter(Boolean);
      const includeAll = projectName === "__all__" || selectedProjects.length === 0;
      const aggregate = rows.reduce<Record<string, TemplateProjectMetric>>((map, row) => {
        if (!includeAll && !selectedProjects.includes(row.project_name)) {
          return map;
        }
        const existing = map[row.template_id];
        if (!existing) {
          map[row.template_id] = { ...row };
          return map;
        }
        const judgeCalls = existing.judge_calls + row.judge_calls;
        const judgeErrors = existing.judge_errors + row.judge_errors;
        existing.avg_latency_ms =
          judgeCalls > 0
            ? (existing.avg_latency_ms * existing.judge_calls + row.avg_latency_ms * row.judge_calls) /
              judgeCalls
            : 0;
        existing.judge_calls = judgeCalls;
        existing.judge_errors = judgeErrors;
        existing.annotations_written += row.annotations_written;
        existing.budget_skips += row.budget_skips;
        existing.error_rate_pct = judgeCalls > 0 ? (judgeErrors / judgeCalls) * 100 : 0;
        return map;
      }, {} as Record<string, TemplateProjectMetric>);
      setTemplateMetrics(aggregate);
    } catch {
      // Metrics are supplementary; a failed poll should not surface a toast.
    }
  }, [projectName]);

  const resolveSelectedProjects = (): string[] => {
    if (projectName === "__all__") {
      return projects.map((project) => project.name);
    }
    return projectName
      .split(",")
      .map((name) => name.trim())
      .filter(Boolean);
  };

  const buildTemplateConfig = (templateId: string, scope: EvaluationScope) => {
    const baseConfig =
      templateId === "contains_substring"
        ? { expected_substring: expectedSubstrings[templateId] ?? "" }
        : templateId === "closed_qa"
          ? { criterion: criterions[templateId] ?? "" }
          : templateId === "perplexity"
            ? { threshold: parseFloat(perplexityThresholds[templateId] || "2.0") }
            : {};
    // NOTE: the judge model selection is intentionally NOT sent here. The live
    // monitoring config is validated against TargetConfig (extra="forbid") and
    // persisted to a plaintext store, so an `eval_model_config` key is rejected
    // with a 422 (and could smuggle provider secrets). Live evaluation uses the
    // deployment-configured evaluator model.
    return { ...baseConfig, level: scope };
  };

  const startMonitoring = async (templateId: string) => {
    const projectNames = resolveSelectedProjects();
    if (projectNames.length === 0) {
      notifyError({
        title: "Project required",
        message: "Select at least one project before starting live evaluation.",
      });
      return;
    }

    setActionPending(templateId);
    setMonitorStatuses((previous) => ({
      ...previous,
      [templateId]: {
        ...(previous[templateId] ?? { template_id: templateId }),
        status: "running",
      },
    }));

    const config = buildTemplateConfig(templateId, evaluationScope);

    try {
      const responses = await Promise.all(
        projectNames.map((name) =>
          fetchApi("/v1/templates/start", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ template_id: templateId, project_name: name, config }),
          })
        )
      );
      const failed = responses.find((response) => !response.ok);
      if (failed) {
        const responseBody = await failed.json().catch(() => null);
        throw new Error(
          extractApiErrorMessage(responseBody?.detail, failed.statusText)
        );
      }
      await fetchMonitorStatuses();
      notifySuccess({
        title: "Live evaluation started",
        message: `Evaluator "${templateId}" is now running on ${projectNames.join(", ")}.`,
      });
    } catch (error) {
      // Re-sync from the server rather than guessing: a partial failure means
      // some (template, project) pairs may have started and others not.
      await fetchMonitorStatuses();
      notifyError({
        title: "Failed to start monitoring",
        message: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setActionPending(null);
    }
  };

  const stopMonitoring = async (templateId: string) => {
    setActionPending(templateId);
    setMonitorStatuses((previous) => ({
      ...previous,
      [templateId]: {
        ...(previous[templateId] ?? { template_id: templateId }),
        status: "stopped",
      },
    }));

    try {
      const response = await fetchApi("/v1/templates/stop", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ template_id: templateId }),
      });
      const responseBody = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(
          extractApiErrorMessage(responseBody?.detail, response.statusText)
        );
      }
      if (responseBody) {
        const updatedStatus = buildMonitorStatus(responseBody);
        if (updatedStatus) {
          setMonitorStatuses((previous) => ({
            ...previous,
            [updatedStatus.template_id]: updatedStatus,
          }));
        }
      }
      await fetchMonitorStatuses();
      notifySuccess({
        title: "Monitoring stopped",
        message: `Evaluator "${templateId}" has been stopped.`,
      });
    } catch (error) {
      // Re-sync: the server is authoritative on whether the stop took effect.
      await fetchMonitorStatuses();
      notifyError({
        title: "Failed to stop monitoring",
        message: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setActionPending(null);
    }
  };

  const startAllMonitoring = async () => {
    const projectNames = resolveSelectedProjects();
    if (projectNames.length === 0) {
      notifyError({
        title: "Project required",
        message: "Select at least one project before starting live evaluation.",
      });
      return;
    }

    const templatesToStart = actionableTemplates.filter((template) => {
      const isRunning =
        normalizeMonitorStatus(monitorStatuses[template.id]?.status) === "running";
      return !isRunning;
    });
    if (templatesToStart.length === 0) {
      return;
    }

    setActionPending("all");
    setMonitorStatuses((previous) => {
      const updated = { ...previous };
      templatesToStart.forEach((t) => {
        updated[t.id] = {
          ...(updated[t.id] ?? { template_id: t.id }),
          status: "running",
        };
      });
      return updated;
    });

    // Bulk start applies one config to every template in the call, so group
    // templates by their resolved config. This keeps per-template inputs
    // (expected_substring, criterion, threshold) while collapsing what was
    // N templates x M projects requests into (#configs x M) bulk calls.
    const groups = new Map<
      string,
      { config: ReturnType<typeof buildTemplateConfig>; templateIds: string[] }
    >();
    templatesToStart.forEach((template) => {
      const config = buildTemplateConfig(template.id, evaluationScope);
      const key = JSON.stringify(config);
      const group = groups.get(key);
      if (group) {
        group.templateIds.push(template.id);
      } else {
        groups.set(key, { config, templateIds: [template.id] });
      }
    });

    const calls = projectNames.flatMap((name) =>
      Array.from(groups.values()).map(({ config, templateIds }) => ({
        projectName: name,
        promise: fetchApi("/v1/templates/start-bulk", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            project_name: name,
            template_ids: templateIds,
            config,
          }),
        }),
      }))
    );

    try {
      // allSettled so one project's network failure doesn't discard the
      // results (and the already-enabled/not-found detail) of the others.
      const settled = await Promise.allSettled(calls.map((call) => call.promise));
      const failures: string[] = [];
      const notFound = new Set<string>();
      const alreadyEnabled = new Set<string>();
      for (let index = 0; index < settled.length; index += 1) {
        const result = settled[index];
        const { projectName } = calls[index];
        if (result.status === "rejected") {
          failures.push(
            `${projectName}: ${
              result.reason instanceof Error
                ? result.reason.message
                : "request failed"
            }`
          );
          continue;
        }
        const body = (await result.value
          .json()
          .catch(() => null)) as BulkStartResponseBody | null;
        if (!result.value.ok) {
          failures.push(
            `${projectName}: ${extractApiErrorMessage(
              body?.detail,
              result.value.statusText
            )}`
          );
          continue;
        }
        body?.not_found?.forEach((id) => notFound.add(id));
        body?.already_enabled?.forEach((id) => alreadyEnabled.add(id));
      }

      if (failures.length > 0 || notFound.size > 0) {
        const parts = [...failures];
        if (notFound.size > 0) {
          parts.push(`unknown template(s): ${[...notFound].join(", ")}`);
        }
        notifyError({
          title: "Some monitors failed to start",
          message: parts.join("; "),
        });
      } else {
        const alreadyNote =
          alreadyEnabled.size > 0
            ? ` ${alreadyEnabled.size} pair(s) were already running and kept their existing configuration.`
            : "";
        notifySuccess({
          title: "All monitors started",
          message: `Started monitoring for ${templatesToStart.length} evaluator(s) across ${projectNames.length} project(s).${alreadyNote}`,
        });
      }
    } catch (error) {
      notifyError({
        title: "Failed to start all monitors",
        message: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      // Always re-sync: a partial failure leaves the server in a state that
      // only /status can describe (per (template, project) pairs).
      await fetchMonitorStatuses();
      setActionPending(null);
    }
  };

  const stopAllMonitoring = async () => {
    const templatesToStop = actionableTemplates.filter((template) => {
      const isRunning =
        normalizeMonitorStatus(monitorStatuses[template.id]?.status) === "running";
      return isRunning;
    });
    if (templatesToStop.length === 0) {
      return;
    }

    setActionPending("all");
    setMonitorStatuses((previous) => {
      const updated = { ...previous };
      templatesToStop.forEach((t) => {
        updated[t.id] = {
          ...(updated[t.id] ?? { template_id: t.id }),
          status: "stopped",
        };
      });
      return updated;
    });

    const projectNames = resolveSelectedProjects();
    const templateIds = templatesToStop.map((template) => template.id);

    try {
      // With an explicit project selection, stop through the bulk endpoint
      // (one call per project). With no selection, fall back to the
      // project-less stop, which stops the template across every project.
      const responses =
        projectNames.length > 0
          ? await Promise.all(
              projectNames.map((name) =>
                fetchApi("/v1/templates/stop-bulk", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ project_name: name, template_ids: templateIds }),
                })
              )
            )
          : await Promise.all(
              templatesToStop.map((template) =>
                fetchApi("/v1/templates/stop", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ template_id: template.id }),
                })
              )
            );

      const errors = responses.filter((response) => !response.ok);
      if (errors.length > 0) {
        notifyError({
          title: "Some monitors failed to stop",
          message: `${errors.length} evaluator(s) could not be stopped.`,
        });
      } else {
        notifySuccess({
          title: "All monitors stopped",
          message: `Stopped monitoring for ${templatesToStop.length} evaluator(s).`,
        });
      }
    } catch (error) {
      notifyError({
        title: "Failed to stop all monitors",
        message: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      // Always re-sync so a partial stop failure is reflected accurately.
      await fetchMonitorStatuses();
      setActionPending(null);
    }
  };

  useEffect(() => {
    if (!category) {
      return;
    }

    async function fetchTemplates() {
      setLoading(true);
      try {
        const response = await authFetch(prependBasename(`/v1/templates/${category}`));
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error(`Template category not found: ${category}`);
          }
          throw new Error(`Unable to load templates: ${response.statusText}`);
        }
        const data = (await response.json()) as EvaluatorTemplate[];
        setTemplates(data);
      } catch (error) {
        notifyError({
          title: "Failed to load evaluator templates",
          message: error instanceof Error ? error.message : "Unknown error",
        });
      } finally {
        setLoading(false);
      }
    }

    fetchTemplates();
    fetchMonitorStatuses();
    fetchTemplateMetrics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, notifyError, fetchMonitorStatuses, fetchTemplateMetrics]);

  useEffect(() => {
    if (!category) {
      return;
    }
    const interval = window.setInterval(() => {
      fetchMonitorStatuses();
      fetchTemplateMetrics();
    }, 15000);
    return () => window.clearInterval(interval);
  }, [category, fetchMonitorStatuses, fetchTemplateMetrics]);

  const actionableTemplates = templates;

  const activeCount = actionableTemplates.filter((template) => {
    const status = monitorStatuses[template.id];
    return (
      normalizeMonitorStatus(status?.status) === "running"
    );
  }).length;

  const availableCount = actionableTemplates.length - activeCount;

  const totalJudgeCalls = Object.values(templateMetrics).reduce(
    (sum, metric) => sum + (metric.judge_calls ?? 0),
    0,
  );
  const totalAnnotationsWritten = Object.values(templateMetrics).reduce(
    (sum, metric) => sum + (metric.annotations_written ?? 0),
    0,
  );
  const totalJudgeErrors = Object.values(templateMetrics).reduce(
    (sum, metric) => sum + (metric.judge_errors ?? 0),
    0,
  );

  if (!category) {
    return (
      <div css={pageContainer}>
        <Text color="text-500">No template category selected.</Text>
      </div>
    );
  }

  const title = categoryTitle(category);
  const isGlobalBusy = actionPending !== null || loading;

  const renderTemplateCard = (template: EvaluatorTemplate) => {
    const status = monitorStatuses[template.id];
    const isRunning =
      normalizeMonitorStatus(status?.status) === "running";
    const isCardBusy =
      actionPending === template.id || actionPending === "all" || loading;
    const metrics = templateMetrics[template.id];
    const isCardActive = isRunning;

    return (
      <div key={template.id} css={[evaluatorCard, isCardActive && activeCardBorder]}>
        <div css={cardHeader}>
          <div css={cardIcon}>
            <Icon svg={iconForCategory(category)} />
          </div>
          <div>
            <Heading level={3}>{template.name}</Heading>
            <Text
              color="text-500"
              css={css`
                font-size: 13px !important;
                line-height: 1.5 !important;
                font-weight: 400 !important;
                color: var(--global-text-color-500, #64748b) !important;
                margin-top: 4px;
                display: block;
              `}
            >
              {template.description}
            </Text>
          </div>
        </div>

        <div css={badgeRow}>
          <span css={chip}>{template.metric_name ?? "Metric"}</span>
          {template.inputs?.map((input) => (
            <span key={input} css={chip}>
              {input}
            </span>
          ))}
          {isRunning ? (
            <span css={statusSuccess}>Running</span>
          ) : (
            <span css={statusStopped}>Stopped</span>
          )}
        </div>

        {isRunning && (
          <>
            <div css={metricRow}>
              <div css={metricCard}>
                <Text size="S" color="text-500">Last check:</Text>
                <Text>
                  {status?.last_check_at
                    ? new Date(status.last_check_at).toLocaleString()
                    : "Waiting"}
                </Text>
              </div>
              <div css={metricCard}>
                <Text size="S" color="text-500">Processed traces:</Text>
                <Text>{status?.processed_traces ?? 0}</Text>
              </div>
            </div>

            <div css={metricRow}>
              <div css={metricCard}>
                <Text size="S" color="text-500">New traces:</Text>
                <Text>{status?.new_traces ?? 0}</Text>
              </div>
              <div css={metricCard}>
                <Text size="S" color="text-500">Annotations:</Text>
                <Text>{status?.annotations_logged ?? 0}</Text>
              </div>
            </div>

            <div css={metricRow}>
              <div css={metricCard}>
                <Text size="S" color="text-500">Judge calls:</Text>
                <Text>{metrics?.judge_calls ?? 0}</Text>
              </div>
              <div css={metricCard}>
                <Text size="S" color="text-500">Judge errors:</Text>
                <Text>{metrics?.judge_errors ?? 0}</Text>
              </div>
            </div>
          </>
        )}

        {isRunning && status?.last_result?.last_evaluation && (
          <div style={{ marginTop: "12px", padding: "12px", borderRadius: "12px", background: "rgba(15, 23, 42, 0.04)", border: "1px solid rgba(15, 23, 42, 0.08)", display: "flex", flexDirection: "column", gap: "6px", width: "100%" }}>
            <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--color-text-600)" }}>Latest Evaluation Result</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              <div>
                <span style={{ fontSize: "0.75rem", color: "var(--color-text-400)", display: "block" }}>Status</span>
                <span style={{ fontSize: "0.875rem", fontWeight: 700, color: status.last_result.last_evaluation.result === "Pass" ? "var(--color-success-700)" : "var(--color-danger-700)" }}>
                  {status.last_result.last_evaluation.result} ({status.last_result.last_evaluation.status})
                </span>
              </div>
              {status.last_result.last_evaluation.score !== undefined && (
                <div>
                  <span style={{ fontSize: "0.75rem", color: "var(--color-text-400)", display: "block" }}>Score</span>
                  <span style={{ fontSize: "0.875rem", fontWeight: 700 }}>{status.last_result.last_evaluation.score}</span>
                </div>
              )}
            </div>
            <div style={{ marginTop: "4px" }}>
              <span style={{ fontSize: "0.75rem", color: "var(--color-text-400)", display: "block" }}>Reason</span>
              <span style={{ fontSize: "0.8125rem", color: "var(--color-text-600)", lineHeight: 1.4 }}>{status.last_result.last_evaluation.reason}</span>
            </div>
          </div>
        )}

        {isRunning && template.id === "is_refusal" && status?.last_result?.summary && (
          <div style={{ marginTop: "12px", padding: "12px", borderRadius: "12px", background: "rgba(15, 23, 42, 0.04)", border: "1px solid rgba(15, 23, 42, 0.08)", display: "flex", flexDirection: "column", gap: "8px", width: "100%" }}>
            <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--color-text-600)" }}>Evaluation Summary</label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px", textAlign: "center" }}>
              <div style={{ padding: "6px", background: "white", borderRadius: "8px", border: "1px solid rgba(15, 23, 42, 0.05)" }}>
                <span style={{ fontSize: "0.7rem", color: "var(--color-text-400)", display: "block" }}>Total</span>
                <span style={{ fontSize: "0.9rem", fontWeight: 700 }}>{status.last_result.summary.total}</span>
              </div>
              <div style={{ padding: "6px", background: "white", borderRadius: "8px", border: "1px solid rgba(15, 23, 42, 0.05)" }}>
                <span style={{ fontSize: "0.7rem", color: "var(--color-text-400)", display: "block" }}>Passed</span>
                <span style={{ fontSize: "0.9rem", fontWeight: 700, color: "var(--color-success-700)" }}>{status.last_result.summary.passed}</span>
              </div>
              <div style={{ padding: "6px", background: "white", borderRadius: "8px", border: "1px solid rgba(15, 23, 42, 0.05)" }}>
                <span style={{ fontSize: "0.7rem", color: "var(--color-text-400)", display: "block" }}>Failed</span>
                <span style={{ fontSize: "0.9rem", fontWeight: 700, color: "var(--color-danger-700)" }}>{status.last_result.summary.failed}</span>
              </div>
            </div>

            {status.last_result.traces && status.last_result.traces.length > 0 && (
              <div style={{ marginTop: "6px" }}>
                <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--color-text-600)", display: "block", marginBottom: "4px" }}>Trace Details</label>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "180px", overflowY: "auto", paddingRight: "4px" }}>
                  {status.last_result.traces.map((t, idx: number) => (
                    <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 8px", background: "white", borderRadius: "6px", border: "1px solid rgba(15, 23, 42, 0.05)", fontSize: "0.75rem" }}>
                      <span style={{ fontFamily: "monospace", color: "var(--color-text-500)" }}>{t.trace_id.slice(0, 8)}...</span>
                      <span style={{ fontWeight: 700, color: t.result === "Pass" ? "var(--color-success-700)" : "var(--color-danger-700)" }}>{t.result}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {isRunning && template.id === "perplexity" && status?.last_result?.summary && (
          <div style={{ marginTop: "12px", padding: "12px", borderRadius: "12px", background: "rgba(15, 23, 42, 0.04)", border: "1px solid rgba(15, 23, 42, 0.08)", display: "flex", flexDirection: "column", gap: "8px", width: "100%" }}>
            <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--color-text-600)" }}>Evaluation Summary (Threshold: {status.last_result.threshold})</label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "6px", textAlign: "center" }}>
              <div style={{ padding: "6px", background: "white", borderRadius: "8px", border: "1px solid rgba(15, 23, 42, 0.05)" }}>
                <span style={{ fontSize: "0.65rem", color: "var(--color-text-400)", display: "block" }}>Total</span>
                <span style={{ fontSize: "0.85rem", fontWeight: 700 }}>{status.last_result.summary.total}</span>
              </div>
              <div style={{ padding: "6px", background: "white", borderRadius: "8px", border: "1px solid rgba(15, 23, 42, 0.05)" }}>
                <span style={{ fontSize: "0.65rem", color: "var(--color-text-400)", display: "block" }}>Passed</span>
                <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "var(--color-success-700)" }}>{status.last_result.summary.passed}</span>
              </div>
              <div style={{ padding: "6px", background: "white", borderRadius: "8px", border: "1px solid rgba(15, 23, 42, 0.05)" }}>
                <span style={{ fontSize: "0.65rem", color: "var(--color-text-400)", display: "block" }}>Failed</span>
                <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "var(--color-danger-700)" }}>{status.last_result.summary.failed}</span>
              </div>
              <div style={{ padding: "6px", background: "white", borderRadius: "8px", border: "1px solid rgba(15, 23, 42, 0.05)" }}>
                <span style={{ fontSize: "0.65rem", color: "var(--color-text-400)", display: "block" }}>Skipped</span>
                <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "var(--color-text-500)" }}>{status.last_result.summary.skipped}</span>
              </div>
            </div>

            {status.last_result.traces && status.last_result.traces.length > 0 && (
              <div style={{ marginTop: "6px" }}>
                <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--color-text-600)", display: "block", marginBottom: "4px" }}>Trace Details</label>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "180px", overflowY: "auto", paddingRight: "4px" }}>
                  {status.last_result.traces.map((t, idx: number) => (
                    <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 8px", background: "white", borderRadius: "6px", border: "1px solid rgba(15, 23, 42, 0.05)", fontSize: "0.75rem" }}>
                      <span style={{ fontFamily: "monospace", color: "var(--color-text-500)" }}>{t.trace_id.slice(0, 8)}...</span>
                      <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                        {t.perplexity !== null && t.perplexity !== undefined && (
                          <span style={{ color: "var(--color-text-400)" }}>ppl: {t.perplexity.toFixed(2)}</span>
                        )}
                        <span style={{ fontWeight: 700, color: t.result === "Pass" ? "var(--color-success-700)" : t.result === "Fail" ? "var(--color-danger-700)" : "var(--color-text-500)" }}>
                          {t.result}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {template.id === "contains_substring" && (
          <div style={{ marginTop: "12px", marginBottom: "12px", display: "flex", flexDirection: "column", gap: "6px", width: "100%" }}>
            <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--color-text-600)" }}>Expected Substring</label>
            <input
              type="text"
              placeholder="Enter expected substring..."
              value={expectedSubstrings[template.id] ?? ""}
              onChange={(e) => setExpectedSubstrings({ ...expectedSubstrings, [template.id]: e.target.value })}
              disabled={isRunning || isCardBusy}
              style={{
                padding: "8px 12px",
                borderRadius: "8px",
                border: "1px solid rgba(15, 23, 42, 0.15)",
                fontSize: "0.875rem",
                width: "100%",
                boxSizing: "border-box"
              }}
            />
          </div>
        )}

        {template.id === "closed_qa" && (
          <div style={{ marginTop: "12px", marginBottom: "12px", display: "flex", flexDirection: "column", gap: "6px", width: "100%" }}>
            <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--color-text-600)" }}>Criterion</label>
            <input
              type="text"
              placeholder="e.g. Is concise, Includes a real-world example"
              value={criterions[template.id] ?? ""}
              onChange={(e) => setCriterions({ ...criterions, [template.id]: e.target.value })}
              disabled={isRunning || isCardBusy}
              style={{
                padding: "8px 12px",
                borderRadius: "8px",
                border: "1px solid rgba(15, 23, 42, 0.15)",
                fontSize: "0.875rem",
                width: "100%",
                boxSizing: "border-box"
              }}
            />
          </div>
        )}

        {template.id === "perplexity" && (
          <div style={{ marginTop: "12px", marginBottom: "12px", display: "flex", flexDirection: "column", gap: "6px", width: "100%" }}>
            <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--color-text-600)" }}>Threshold</label>
            <input
              type="text"
              placeholder="e.g. 2.0"
              value={perplexityThresholds[template.id] ?? ""}
              onChange={(e) => setPerplexityThresholds({ ...perplexityThresholds, [template.id]: e.target.value })}
              disabled={isRunning || isCardBusy}
              style={{
                padding: "8px 12px",
                borderRadius: "8px",
                border: "1px solid rgba(15, 23, 42, 0.15)",
                fontSize: "0.875rem",
                width: "100%",
                boxSizing: "border-box"
              }}
            />
          </div>
        )}

        <div style={{ fontSize: "0.75rem", color: "var(--color-text-500)" }}>
          Scope:{" "}
          {isRunning
            ? describeTargetScope(monitorStatuses[template.id]?.level, evaluationScope)
            : scopeLabel(evaluationScope)}
        </div>

        <div css={actionRow}>
          {isRunning ? (
            <Button
              size="S"
              variant="default"
              onClick={() => stopMonitoring(template.id)}
              isDisabled={isCardBusy}
            >
              {actionPending === template.id ? "Stopping…" : "Stop Live Evaluation"}
            </Button>
          ) : (
            <Button
              size="S"
              variant="primary"
              onClick={() => startMonitoring(template.id)}
              isDisabled={isCardBusy || projectName.trim() === ""}
            >
              {actionPending === template.id ? "Starting…" : "Start Live Evaluation"}
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div css={pageContainer}>
      <div css={breadcrumbRow}>
        <Link css={breadcrumbLink} to="/templates">
          Templates
        </Link>
        <Text color="text-400">/</Text>
        <Text color="text-500">{title}</Text>
      </div>

      <div css={headerRow}>
        <div css={categoryIntro}>
          <Heading level={1}>{title} Evaluators</Heading>
          <Text
            color="text-500"
            css={css`
              font-size: 13px !important;
              line-height: 1.5 !important;
              font-weight: 400 !important;
              color: var(--global-text-color-500, #64748b) !important;
              margin-top: var(--global-dimension-size-25);
            `}
          >
            {categoryDescription(category)}
          </Text>
        </div>

        <div css={topActions}>
          <div css={actionRow}>
            <Button
              size="S"
              variant="primary"
              onClick={startAllMonitoring}
              isDisabled={isGlobalBusy || projectName.trim() === "" || availableCount === 0}
            >
              Start All
            </Button>
            <Button
              size="S"
              variant="default"
              onClick={stopAllMonitoring}
              isDisabled={isGlobalBusy || activeCount === 0}
            >
              Stop All
            </Button>
          </div>
          <LinkButton to="/templates" variant="default">
            Back to templates
          </LinkButton>
        </div>
      </div>

      <div css={sectionCard}>
        <div css={fieldWrapper}>
          <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Label css={labelStyle}>Project Name</Label>
            <DialogTrigger>
              <Button style={{ width: "100%", justifyContent: "space-between" }}>
                {projectName === "__all__" ? (
                  <span>🌐 All Projects</span>
                ) : projectName.trim() ? (
                  projectName.includes(",") ? (
                    <span>{projectName.split(",").filter(Boolean).length} Projects Selected</span>
                  ) : (
                    <span>{projectName}</span>
                  )
                ) : (
                  <span>Select Projects</span>
                )}
                <SelectChevronUpDownIcon />
              </Button>
              <Popover placement="bottom start">
                <div
                  style={{
                    background: "var(--global-background-color-default, var(--spectrum-alias-background-color-default, #fff))",
                    border: "1px solid var(--global-border-color-default, var(--global-color-gray-300, rgba(15, 23, 42, 0.15)))",
                    color: "var(--global-text-color-default)",
                    borderRadius: "8px",
                    boxShadow: "var(--ac-premium-shadow-elevated, 0 4px 12px rgba(0, 0, 0, 0.08))",
                    padding: "8px",
                    minWidth: "240px",
                    maxHeight: "320px",
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <div
                    style={{
                      padding: "6px 8px",
                      borderBottom: "1px solid var(--global-color-gray-200, rgba(15, 23, 42, 0.08))",
                      marginBottom: "4px",
                    }}
                  >
                    <Checkbox
                      isSelected={projectName === "__all__"}
                      onChange={(selected) => {
                        if (selected) {
                          setProjectName("__all__");
                          if (category) {
                            storeValue(`monitor_project_name_${category}`, "__all__");
                          }
                        } else {
                          setProjectName("");
                          if (category) {
                            storeValue(`monitor_project_name_${category}`, "");
                          }
                        }
                      }}
                    >
                      🌐 All Projects
                    </Checkbox>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                    {projects.map((p) => {
                      const isAllSelected = projectName === "__all__";
                      const isSelected = isAllSelected || projectName.split(",").filter(Boolean).includes(p.name);
                      return (
                        <div key={p.name} style={{ padding: "4px 8px" }}>
                          <Checkbox
                            isSelected={isSelected}
                            onChange={(selected) => {
                              let newSelection = "";
                              let currentSelected = isAllSelected
                                ? projects.map((proj) => proj.name)
                                : projectName.split(",").filter(Boolean);

                              if (selected) {
                                if (!currentSelected.includes(p.name)) {
                                  currentSelected.push(p.name);
                                }
                              } else {
                                currentSelected = currentSelected.filter((name) => name !== p.name);
                              }

                              if (currentSelected.length === 0) {
                                newSelection = "";
                              } else if (currentSelected.length === projects.length) {
                                newSelection = "__all__";
                              } else {
                                newSelection = currentSelected.join(",");
                              }

                              setProjectName(newSelection);
                              if (category) {
                                storeValue(`monitor_project_name_${category}`, newSelection);
                              }
                            }}
                          >
                            {p.name}
                          </Checkbox>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Popover>
            </DialogTrigger>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              marginTop: "var(--global-dimension-size-75, 12px)",
              width: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              <Label css={labelStyle}>Evaluation scope</Label>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "4px 10px",
                  borderRadius: "9999px",
                  background: "rgba(59, 130, 246, 0.08)",
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "9999px",
                    background: "var(--ac-global-color-primary, #3b82f6)",
                  }}
                />
                {scopeLabel(evaluationScope)}
              </span>
              <Link to="/templates">Change on Templates page</Link>
            </div>
          </div>

          {projectName !== "__all__" && projectName.trim() !== "" && (
            <div css={selectedProjectsContainer}>
              <div css={selectedProjectsHeader}>
                <span css={selectedProjectsTitle}>
                  Selected Projects ({projectName.split(",").filter(Boolean).length})
                </span>
                <button
                  type="button"
                  css={clearAllButtonStyle}
                  onClick={() => {
                    setProjectName("__all__");
                    if (category) {
                      storeValue(`monitor_project_name_${category}`, "__all__");
                    }
                  }}
                >
                  <Icon svg={<Icons.TrashOutline />} css={trashIconStyle} />
                  Clear all
                </button>
              </div>
              <div css={badgesWrapper}>
                {projectName.split(",").filter(Boolean).map((projName) => (
                  <span key={projName} css={badgeItemStyle}>
                    <Icon svg={<Icons.FolderOutline />} css={folderIconStyle} />
                    <span>{projName}</span>
                    <button
                      type="button"
                      css={badgeRemoveButtonStyle}
                      onClick={() => {
                        const currentSelected = projectName.split(",").filter(Boolean);
                        const updated = currentSelected.filter((p) => p !== projName);
                        const newSelection = updated.length > 0 ? updated.join(",") : "__all__";
                        setProjectName(newSelection);
                        if (category) {
                          storeValue(`monitor_project_name_${category}`, newSelection);
                        }
                      }}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* â”€â”€ Evaluation Model Section â”€â”€ */}
          <div style={{ marginTop: "24px", paddingTop: "20px", borderTop: "1px solid var(--global-border-color-default, rgba(15, 23, 42, 0.08))" }}>
            <div style={{ marginBottom: "16px" }}>
              <Heading level={3} style={{ margin: 0, fontSize: "1.125rem", fontWeight: 600 }}>Evaluation Model</Heading>
              <Text color="text-500" size="S" style={{ marginTop: "4px", display: "block" }}>
                Configure the AI provider and model used by evaluators in this category.
              </Text>
            </div>

            {/* Provider & Model Row */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "20px", marginBottom: "16px" }}>
              <div>
                <Label css={labelStyle}>Provider</Label>
                <select
                  value={evalModelConfig.provider}
                  onChange={(e) => handleProviderChange(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: "1px solid var(--global-border-color-default, rgba(15, 23, 42, 0.15))",
                    background: "var(--global-background-color-default, #fff)",
                    color: "var(--global-text-color-default, #0f172a)",
                    fontSize: "0.875rem",
                    boxSizing: "border-box",
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  {Object.entries(PROVIDER_METADATA).map(([key, meta]) => (
                    <option key={key} value={key}>
                      {meta.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                  <Label css={labelStyle} style={{ margin: 0 }}>Model</Label>
                  <button
                    type="button"
                    onClick={() => setIsCustomModel(!isCustomModel)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--global-color-blue-600, #2563eb)",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      cursor: "pointer",
                      padding: 0,
                    }}
                  >
                    {isCustomModel ? "Select Preset Model" : "Custom Model"}
                  </button>
                </div>
                {isCustomModel ? (
                  <input
                    type="text"
                    value={evalModelConfig.model ?? ""}
                    onChange={(e) => setEvalModelConfig((prev) => ({ ...prev, model: e.target.value }))}
                    placeholder="Enter model name (e.g. gemini-2.5-flash)"
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      borderRadius: "8px",
                      border: "1px solid var(--global-border-color-default, rgba(15, 23, 42, 0.15))",
                      background: "var(--global-background-color-default, #fff)",
                      color: "var(--global-text-color-default, #0f172a)",
                      fontSize: "0.875rem",
                      boxSizing: "border-box",
                      outline: "none",
                    }}
                  />
                ) : (
                  <select
                    value={evalModelConfig.model}
                    onChange={(e) => setEvalModelConfig((prev) => ({ ...prev, model: e.target.value }))}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      borderRadius: "8px",
                      border: "1px solid var(--global-border-color-default, rgba(15, 23, 42, 0.15))",
                      background: "var(--global-background-color-default, #fff)",
                      color: "var(--global-text-color-default, #0f172a)",
                      fontSize: "0.875rem",
                      boxSizing: "border-box",
                      cursor: "pointer",
                      outline: "none",
                    }}
                  >
                    {(PROVIDER_METADATA[evalModelConfig.provider]?.models || [evalModelConfig.model]).map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {/* API Key & Base URL Row */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "20px", marginBottom: "16px" }}>
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                  <Label css={labelStyle} style={{ margin: 0 }}>API Key</Label>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => setShowApiKey(!showApiKey)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setShowApiKey(!showApiKey); }}
                    style={{
                      color: "var(--global-color-blue-600, #2563eb)",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    {showApiKey ? "Hide Key" : "Show Key"}
                  </span>
                </div>
                <input
                  type={showApiKey ? "text" : "password"}
                  value={evalModelConfig.apiKey ?? ""}
                  onChange={(e) => setEvalModelConfig((prev) => ({ ...prev, apiKey: e.target.value }))}
                  placeholder="Enter API key or leave blank for env default..."
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: "1px solid var(--global-border-color-default, rgba(15, 23, 42, 0.15))",
                    background: "var(--global-background-color-default, #fff)",
                    color: "var(--global-text-color-default, #0f172a)",
                    fontSize: "0.875rem",
                    boxSizing: "border-box",
                    outline: "none",
                  }}
                />
              </div>

              <div>
                <Label css={labelStyle}>Base URL</Label>
                <input
                  type="text"
                  value={evalModelConfig.baseUrl ?? ""}
                  onChange={(e) => setEvalModelConfig((prev) => ({ ...prev, baseUrl: e.target.value }))}
                  placeholder="e.g. https://generativelanguage.googleapis.com/v1beta/openai/"
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: "1px solid var(--global-border-color-default, rgba(15, 23, 42, 0.15))",
                    background: "var(--global-background-color-default, #fff)",
                    color: "var(--global-text-color-default, #0f172a)",
                    fontSize: "0.875rem",
                    boxSizing: "border-box",
                    outline: "none",
                  }}
                />
              </div>
            </div>

            {/* Advanced Configuration Accordion */}
            <div style={{ marginBottom: "16px" }}>
              <button
                type="button"
                onClick={() => setShowAdvancedConfig(!showAdvancedConfig)}
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--global-color-blue-600, #2563eb)",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  cursor: "pointer",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <span>{showAdvancedConfig ? "▼" : "▶"}</span>
                <span>{showAdvancedConfig ? "Hide Advanced Configuration" : "Show Advanced Configuration"}</span>
              </button>

              {showAdvancedConfig && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: "16px",
                    marginTop: "12px",
                    padding: "16px",
                    background: "rgba(15, 23, 42, 0.02)",
                    borderRadius: "8px",
                    border: "1px solid var(--global-border-color-default, rgba(15, 23, 42, 0.06))",
                  }}
                >
                  <div>
                    <Label css={labelStyle}>Temperature: {evalModelConfig.temperature}</Label>
                    <input
                      type="range"
                      min="0"
                      max="2"
                      step="0.05"
                      value={evalModelConfig.temperature}
                      onChange={(e) => setEvalModelConfig((prev) => ({ ...prev, temperature: parseFloat(e.target.value) }))}
                      style={{ width: "100%", cursor: "pointer" }}
                    />
                  </div>

                  <div>
                    <Label css={labelStyle}>Max Completion Tokens</Label>
                    <input
                      type="number"
                      value={evalModelConfig.maxCompletionTokens}
                      onChange={(e) => setEvalModelConfig((prev) => ({ ...prev, maxCompletionTokens: parseInt(e.target.value, 10) || 0 }))}
                      placeholder="1000"
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        borderRadius: "8px",
                        border: "1px solid var(--global-border-color-default, rgba(15, 23, 42, 0.15))",
                        background: "var(--global-background-color-default, #fff)",
                        color: "var(--global-text-color-default, #0f172a)",
                        fontSize: "0.875rem",
                        boxSizing: "border-box",
                        outline: "none",
                      }}
                    />
                  </div>

                  <div>
                    <Label css={labelStyle}>Top P</Label>
                    <input
                      type="number"
                      step="0.05"
                      min="0"
                      max="1"
                      value={evalModelConfig.topP}
                      onChange={(e) => setEvalModelConfig((prev) => ({ ...prev, topP: parseFloat(e.target.value) || 0 }))}
                      placeholder="1.0"
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        borderRadius: "8px",
                        border: "1px solid var(--global-border-color-default, rgba(15, 23, 42, 0.15))",
                        background: "var(--global-background-color-default, #fff)",
                        color: "var(--global-text-color-default, #0f172a)",
                        fontSize: "0.875rem",
                        boxSizing: "border-box",
                        outline: "none",
                      }}
                    />
                  </div>

                  <div>
                    <Label css={labelStyle}>Seed [Optional]</Label>
                    <input
                      type="text"
                      value={evalModelConfig.seed}
                      onChange={(e) => setEvalModelConfig((prev) => ({ ...prev, seed: e.target.value }))}
                      placeholder="e.g. 42"
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        borderRadius: "8px",
                        border: "1px solid var(--global-border-color-default, rgba(15, 23, 42, 0.15))",
                        background: "var(--global-background-color-default, #fff)",
                        color: "var(--global-text-color-default, #0f172a)",
                        fontSize: "0.875rem",
                        boxSizing: "border-box",
                        outline: "none",
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Validation Feedback & Action Button */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "12px" }}>
              <div>
                {validationStatus && (
                  <span
                    style={{
                      fontSize: "0.8125rem",
                      fontWeight: 500,
                      color: validationStatus.success ? "var(--color-success-700, #15803d)" : "var(--color-danger-700, #b91c1c)",
                    }}
                  >
                    {validationStatus.success ? "✓ " : "✕ "}
                    {validationStatus.message}
                  </span>
                )}
              </div>
              <Button
                variant="default"
                size="S"
                onClick={handleValidateModel}
                isDisabled={isValidatingModel}
                style={{
                  border: "1px solid var(--global-border-color-default, rgba(15, 23, 42, 0.2))",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                {isValidatingModel ? "Validating..." : "Validate Configuration"}
              </Button>
            </div>
          </div>

        </div>
      </div>

      {loading ? (
        <div css={css`min-height: 260px; display: grid; place-items: center;`}>
          <Loading />
        </div>
      ) : templates.length === 0 ? (
        <Alert variant="warning" banner icon={<Icon svg={<Icons.AlertTriangleFilled />} />}>
          No evaluators are available for this category.
        </Alert>
      ) : (
        <div css={pageGrid}>
          <div css={mainContent}>
            <div css={summaryRow}>
              <div css={summaryCard}>
                <Text
                  css={css`text-transform: uppercase; letter-spacing: 0.08em; font-size: 0.75rem; color: var(--color-text-400);`}
                >
                  Overview
                </Text>
                <Heading level={3}>Monitoring at a glance</Heading>
                <div css={metricRow}>
                  <div css={metricCard}>
                    <Text size="S" color="text-500">Total evaluators:</Text>
                    <Text>{templates.length}</Text>
                  </div>
                  <div css={metricCard}>
                    <Text size="S" color="text-500">Stopped:</Text>
                    <Text>{availableCount}</Text>
                  </div>
                </div>
                <div css={metricRow}>
                  <div css={metricCard}>
                    <Text size="S" color="text-500">Judge calls:</Text>
                    <Text>{totalJudgeCalls}</Text>
                  </div>
                  <div css={metricCard}>
                    <Text size="S" color="text-500">Annotations written:</Text>
                    <Text>{totalAnnotationsWritten}</Text>
                  </div>
                </div>
                <div css={metricRow}>
                  <div css={metricCard}>
                    <Text size="S" color="text-500">Judge errors:</Text>
                    <Text>{totalJudgeErrors}</Text>
                  </div>
                  <div css={metricCard}>
                    <Text size="S" color="text-500">Manual monitors running:</Text>
                    <Text>{activeCount}</Text>
                  </div>
                </div>
              </div>
            </div>

            {/* â”€â”€ Grid of Evaluators â”€â”€ */}
            {category === "rag_evaluation" ? (
              <>
                <div css={sectionCard}>
                  <div css={sectionLabel}>Retrieval Evaluations</div>
                  <div css={cardGrid}>
                    {templates
                      .filter((t) => ["context_relevance", "recall", "precision", "mrr", "diversity", "coverage"].includes(t.id))
                      .map((template) => renderTemplateCard(template))}
                  </div>
                </div>
                <div css={[sectionCard, css`margin-top: var(--global-dimension-size-100);`]}>
                  <div css={sectionLabel}>Grounding Evaluations</div>
                  <div css={cardGrid}>
                    {templates
                      .filter((t) => ["groundedness", "faithfulness", "evidence_alignment"].includes(t.id))
                      .map((template) => renderTemplateCard(template))}
                  </div>
                </div>
                <div css={[sectionCard, css`margin-top: var(--global-dimension-size-100);`]}>
                  <div css={sectionLabel}>Generation Evaluations</div>
                  <div css={cardGrid}>
                    {templates
                      .filter((t) => ["answer_relevance", "completeness", "conciseness", "clarity", "coherence"].includes(t.id))
                      .map((template) => renderTemplateCard(template))}
                  </div>
                </div>
                <div css={[sectionCard, css`margin-top: var(--global-dimension-size-100);`]}>
                  <div css={sectionLabel}>Hallucination Evaluations</div>
                  <div css={cardGrid}>
                    {templates
                      .filter((t) => ["hallucination_detection", "unsupported_claims", "overgeneralization"].includes(t.id))
                      .map((template) => renderTemplateCard(template))}
                  </div>
                </div>
              </>
            ) : category === "sql_evaluation" ? (
              <>
                <div css={sectionCard}>
                  <div css={sectionLabel}>Sql Evaluations</div>
                  <div css={cardGrid}>
                    {templates
                      .filter((t) => ["sql_evaluation"].includes(t.id))
                      .map((template) => renderTemplateCard(template))}
                  </div>
                </div>
              </>
            ) : category === "deterministic_metrics" ? (
              <>
                <div css={sectionCard}>
                  <div css={sectionLabel}>Deterministic Metrics</div>
                  <div css={cardGrid}>
                    {templates
                      .filter((t) => ["contains_substring", "is_refusal", "perplexity"].includes(t.id))
                      .map((template) => renderTemplateCard(template))}
                  </div>
                </div>
              </>
            ) : category === "model_graded_metrics" ? (
              <>
                <div css={sectionCard}>
                  <div css={sectionLabel}>Model-graded metrics</div>
                  <div css={cardGrid}>
                    {templates
                      .filter((t) => ["moderation", "closed_qa"].includes(t.id))
                      .map((template) => renderTemplateCard(template))}
                  </div>
                </div>
              </>
            ) : category === "code_generation" ? (
              <>
                <div css={sectionCard}>
                  <div css={sectionLabel}>Code Generation Evaluations</div>
                  <div css={cardGrid}>
                    {templates
                      .filter((t) => ["Syntax_Correctness", "Compilation_Success", "Efficiency_of_Code", "Test_Case_Pass_Rate", "Security_Vulnerabilities"].includes(t.id))
                      .map((template) => renderTemplateCard(template))}
                  </div>
                </div>
              </>
            ) : (
              <div css={sectionCard}>
                <div css={sectionLabel}>Evaluator Templates</div>
                <div css={cardGrid}>
                  {templates.map((template) => renderTemplateCard(template))}
                </div>
              </div>
            )}
          </div>

          {/* â”€â”€ Right sidebar â”€â”€ */}
          <aside css={summaryPanel}>
            <div css={summaryCard}>
              <div css={sectionLabel}>Live activity</div>
              <Heading level={3}>Recent evaluator events</Heading>
              <Text color="text-500">
                Auto-refreshes every 15 seconds to keep status current.
              </Text>

              <div css={liveActivityList}>
                {liveActivity.length === 0 ? (
                  <div css={emptyState}>
                    <Icon svg={<Icons.Info />} />
                    <Text color="text-500">
                      Live activity will appear once monitoring starts.
                    </Text>
                  </div>
                ) : (
                  liveActivity.slice(0, 6).map((entry) => (
                    <div key={entry.id} css={liveActivityItem}>
                      <div css={liveActivityHeader}>
                        <Heading level={4}>{entry.title}</Heading>
                        <Text css={timestampLabel}>
                          {new Date(entry.timestamp).toLocaleTimeString()}
                        </Text>
                      </div>
                      <Text color={entry.status === "success" ? "text-900" : "text-500"}>
                        {entry.description}
                      </Text>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div css={summaryCard}>
              <div css={sectionLabel}>Monitoring summary</div>
              <Heading level={3}>Current template health</Heading>
              <Text color="text-500">Live view of monitor status and trace activity.</Text>

              <div css={metricRow}>
                <div css={metricCard}>
                  <Text size="S" color="text-500">Stopped:</Text>
                  <Text>{availableCount}</Text>
                </div>
                <div css={metricCard}>
                  <Text size="S" color="text-500">Live evaluations running:</Text>
                  <Text>{activeCount}</Text>
                </div>
              </div>

              <div css={metricRow}>
                <div css={metricCard}>
                  <Text size="S" color="text-500">Judge calls:</Text>
                  <Text>{totalJudgeCalls}</Text>
                </div>
                <div css={metricCard}>
                  <Text size="S" color="text-500">Annotations written:</Text>
                  <Text>{totalAnnotationsWritten}</Text>
                </div>
              </div>

              <div css={metricRow}>
                <div css={metricCard}>
                  <Text size="S" color="text-500">Judge errors:</Text>
                  <Text>{totalJudgeErrors}</Text>
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
